import { z } from 'zod';

/**
 * TBO Flight provider (live search only - no local mirror).
 *
 * Why no mirror: fares change continuously. Each picker request hits the
 * TBO Air API directly. Stream B Phase B1.5.
 *
 * Auth flow (handled in `TboFlightApiClient`):
 *   1. `POST /Authenticate` returns a TokenId valid ~23h. Cached in Redis.
 *   2. Every subsequent call (Search / FareUpsell / FareRule / FareQuote) sends the
 *      cached TokenId. On 401/expiry we re-authenticate.
 */

// ---- Search input -------------------------------------------------------

export const TboFlightCabinClassSchema = z.enum([
  'ECONOMY',
  'PREMIUM_ECONOMY',
  'BUSINESS',
  'FIRST',
]);
export type TboFlightCabinClass = z.infer<typeof TboFlightCabinClassSchema>;

/**
 * "Special fare" concession the traveller claims, matching the chip row on the
 * search widget. Optional everywhere - omitting it searches regular fares.
 *
 * Maps to TBO's `ResultFareType` on `Search` (Air API v10, Search FAQ Q4:
 * 2 RegularFare, 3 StudentFare, 4 Armed Force, 5 Senior Citizen). TBO offers no
 * doctor/nurse fare, so there is no such value here. Eligibility is the
 * airline's call at check-in (student ID, age proof, defence ID); a search may
 * also simply return regular fares when no carrier on the route offers the
 * concession, which is why each result carries its own `fareType`.
 */
export const TboFlightPassengerTypeSchema = z.enum([
  'REGULAR',
  'STUDENT',
  'SENIOR_CITIZEN',
  'ARMED_FORCES',
]);
export type TboFlightPassengerType = z.infer<typeof TboFlightPassengerTypeSchema>;

/** One leg of a flight search: origin -> destination on a date. */
export const TboFlightSearchSegmentSchema = z.object({
  /** Origin IATA airport code (e.g. "BOM"). */
  origin: z.string().trim().length(3),
  /** Destination IATA airport code. */
  destination: z.string().trim().length(3),
  /** YYYY-MM-DD departure date for this leg. */
  departureDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export type TboFlightSearchSegment = z.infer<typeof TboFlightSearchSegmentSchema>;

export const TboFlightSearchInputSchema = z.object({
  /**
   * The legs to search. JourneyType is inferred server-side per TBO:
   *   1 segment            -> one-way (JourneyType 1)
   *   2 symmetric segments -> round-trip (JourneyType 2)
   *   anything else (open-jaw / 3+ legs) -> multi-city (JourneyType 3)
   * LCC fares that come back split per leg are cross-combined into joint
   * options server-side, so the client always gets whole journeys.
   */
  segments: z.array(TboFlightSearchSegmentSchema).min(1).max(6),
  adults: z.number().int().min(1).max(9),
  children: z.number().int().min(0).max(9).default(0),
  infants: z.number().int().min(0).max(9).default(0),
  cabinClass: TboFlightCabinClassSchema.default('ECONOMY'),
  /** Optional: prefer a specific airline (IATA two-letter code). */
  preferredAirline: z.string().trim().length(2).optional(),
  /** Direct-flights-only filter. */
  directOnly: z.boolean().default(false),
  /**
   * Optional special-fare concession (student / senior citizen / armed
   * forces). Omitted or `REGULAR` searches regular fares and sends no
   * `ResultFareType` upstream, so the default request is unchanged.
   */
  passengerType: TboFlightPassengerTypeSchema.optional(),
  /** Currency the prices should be returned in (converted live). Default INR. */
  currencyCode: z.string().trim().length(3).default('INR'),
  /** Audit linkage: the lead this search is attributed to (optional). */
  leadId: z.string().uuid().optional(),
  /** Audit linkage: the itinerary this search is attributed to (optional). */
  itineraryId: z.string().uuid().optional(),
  /** Where the search originated, for audit attribution. SYSTEM is server-only,
   *  so it is intentionally excluded from this client-facing enum. */
  source: z.enum(['MANUAL_PICKER', 'AUTOFILL', 'REPRICE']).optional(),
});
export type TboFlightSearchInput = z.infer<typeof TboFlightSearchInputSchema>;

// ---- Airport / city lookup (public search widget) -----------------------

export const FlightAirportQuerySchema = z.object({
  /** Free-text: matches city name prefix, or an exact IATA code. */
  q: z.string().trim().max(60).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(15),
});
export type FlightAirportQuery = z.infer<typeof FlightAirportQuerySchema>;

/**
 * One selectable origin/destination for the search widget.
 *
 * Backed by the City master (`cities.iata_code`) plus a curated multi-airport
 * metro catalog. Multi-airport cities (London, NYC, …) expand into a `city`
 * parent row ("All Airports") followed by `airport` children — the ATW-style
 * menu / sub-menu. A full airport master can replace the catalog later without
 * changing this shape.
 */
export const FlightAirportItemSchema = z.object({
  /** IATA code used as the TBO `Origin` / `Destination` value. */
  iataCode: z.string(),
  cityName: z.string(),
  /**
   * Specific airport label (e.g. "London Heathrow Airport"). Null on the
   * `city` "All Airports" parent and on single-airport city rows that still
   * only carry a city name.
   */
  airportName: z.string().nullable().default(null),
  countryName: z.string().nullable(),
  /** ISO-3166-1 alpha-2 where the country master carries one. */
  countryCode: z.string().nullable(),
  /** Popular cities sort first and seed the empty-query list. */
  isPopular: z.boolean(),
  /**
   * `city` = "London (All Airports)" parent (uses the metro hub IATA).
   * `airport` = a specific airport, or a single-airport city row.
   */
  kind: z.enum(['city', 'airport']).default('airport'),
});
export type FlightAirportItem = z.infer<typeof FlightAirportItemSchema>;

export const FlightAirportListResponseSchema = z.object({
  data: z.array(FlightAirportItemSchema),
});
export type FlightAirportListResponse = z.infer<typeof FlightAirportListResponseSchema>;

// ---- Flight option (normalised for the picker) --------------------------

export const TboFlightSegmentSchema = z.object({
  /** Carrier IATA code. */
  airline: z.string(),
  /** Carrier full name (e.g. "VietJet Air"); optional for back-compat. */
  airlineName: z.string().nullable().optional(),
  flightNumber: z.string(),
  /** Origin airport IATA code. */
  origin: z.string(),
  originAirport: z.string().nullable().optional(),
  originCity: z.string().nullable().optional(),
  originTerminal: z.string().nullable().optional(),
  /** Destination airport IATA code. */
  destination: z.string(),
  destinationAirport: z.string().nullable().optional(),
  destinationCity: z.string().nullable().optional(),
  destinationTerminal: z.string().nullable().optional(),
  /** ISO timestamp; TBO returns local time, we keep it as a string. */
  departureAt: z.string(),
  arrivalAt: z.string(),
  /** Duration in minutes. */
  durationMinutes: z.number().int().nonnegative(),
  cabinClass: TboFlightCabinClassSchema,
  /** Included CHECK-IN allowance, in the airline's own words ("15 KG"). */
  baggageAllowance: z.string().nullable(),
  /**
   * Included CABIN allowance and the aircraft type, both optional.
   *
   * Nullable AND optional because a session snapshot written before these
   * existed is still read back through this schema - a required field would
   * fail an in-flight booking on parse.
   */
  cabinBaggage: z.string().nullable().optional(),
  aircraft: z.string().nullable().optional(),
  /**
   * Seats TBO still reports free on this segment (`NoOfSeatAvailable`). Drives
   * the "N seats left" urgency badge. Null when the supplier omits it.
   */
  seatsLeft: z.number().int().nonnegative().nullable().default(null),
});
export type TboFlightSegment = z.infer<typeof TboFlightSegmentSchema>;

export const TboFlightOptionSchema = z.object({
  /**
   * One ResultIndex per leg, required for the subsequent FareRule / FareQuote /
   * booking. A GDS combined fare carries a single index shared by all legs; an
   * LCC fare carries one index per leg. Length matches `legs` (or is 1).
   */
  resultIndexes: z.array(z.string()).min(1),
  /**
   * Per-leg segment arrays: `[outbound]` one-way, `[outbound, return]`
   * round-trip / open-jaw, `[leg1, leg2, ...]` multi-city.
   */
  legs: z.array(z.array(TboFlightSegmentSchema).min(1)).min(1),
  /** Numeric-string paise (see tbo-hotel.ts for the rationale; same here). */
  totalFarePaise: z.string().regex(/^-?\d+$/, 'totalFarePaise must be a whole-number string'),
  /** Supplier base fare, in paise. Kept alongside `totalFarePaise` for
   *  reconciliation and for the booking funnel's own ladder. */
  basePaise: z.string().regex(/^-?\d+$/, 'basePaise must be a whole-number string'),
  /** Supplier taxes/surcharges, in paise. `basePaise + taxPaise` sums to the
   *  supplier net - and, on the public routes, to `totalFarePaise` too: price
   *  discovery publishes the airline's fare and adds nothing. Our "Convenience
   *  Fees" line is introduced once, on the booking session. */
  taxPaise: z.string().regex(/^-?\d+$/, 'taxPaise must be a whole-number string'),
  currencyCode: z.string(),
  isRefundable: z.boolean(),
  /** Fare basis label TBO returns; surfaced in the picker for ops. */
  fareBasis: z.string().nullable(),
  /**
   * TBO branded-fare name (`FareClassification.Type`, e.g. "SAVER", "PUBLISHED",
   * "CORPORATE") when the search result carries one. Lets the card badge the
   * fare tier without a separate FareUpsell round-trip. Null when absent.
   */
  fareClassification: z.string().nullable().default(null),
  /**
   * Minimum seats still available across all segments of this option (the
   * binding constraint for how many can book). Drives "N seats left". Null when
   * the supplier omits seat counts.
   */
  seatsLeft: z.number().int().nonnegative().nullable().default(null),
  /**
   * What TBO says this fare is (`ResultFareType`): REGULAR, or the concession
   * it was priced under. Null when the supplier did not say. A concession
   * search can still return REGULAR rows - the UI uses this, not the request,
   * to decide whether to promise a discount or ask for ID.
   */
  fareType: TboFlightPassengerTypeSchema.nullable().optional(),
});
export type TboFlightOption = z.infer<typeof TboFlightOptionSchema>;

/**
 * A stable identity for the FLIGHT (not the fare) of an option: airline + flight
 * number + local departure/arrival of every segment across every leg. Two
 * options with the same signature are the same journey at different fares, so
 * the results UI groups by this to show one card per flight (with the fares as
 * selectable tiers) and the API drops exact-duplicate fares within a signature.
 * Shared so the client grouping key and the server dedupe key can never drift.
 */
export function flightSignature(option: Pick<TboFlightOption, 'legs'>): string {
  return option.legs
    .map((leg) =>
      leg.map((s) => `${s.airline}${s.flightNumber}@${s.departureAt}>${s.arrivalAt}`).join('+'),
    )
    .join('|');
}

export const TboFlightSearchResponseSchema = z.object({
  options: z.array(TboFlightOptionSchema),
  traceId: z.string().nullable(),
  /**
   * Legs (by origin/destination/date) that came back with ZERO flights. For a
   * multi-leg / open-jaw search the whole journey is a cross-product of the
   * legs, so ONE empty leg makes `options` empty. Surfacing the culprit lets
   * the UI say "no flights for PQC -> DEL on 31 Aug" instead of a blank list.
   */
  emptyLegs: z.array(TboFlightSearchSegmentSchema).default([]),
  /**
   * True when a special-fare (concession) search returned nothing and we
   * silently re-ran it as a REGULAR fare so the traveller still sees flights.
   * Concession fares (Senior / Armed-Forces / Student) only exist on domestic
   * Indian sectors, so an international concession search would otherwise come
   * back empty. The UI shows a "no concession fares - showing regular fares"
   * note when this is set.
   */
  concessionFallback: z.boolean().default(false),
});
export type TboFlightSearchResponse = z.infer<typeof TboFlightSearchResponseSchema>;

// ---- Fare rule / quote --------------------------------------------------

export const TboFlightFareRuleInputSchema = z.object({
  resultIndex: z.string().trim().min(1),
  traceId: z.string().trim().min(1),
});
export type TboFlightFareRuleInput = z.infer<typeof TboFlightFareRuleInputSchema>;

/**
 * One structured penalty band from TBO's `MiniFareRules`.
 *
 * The same facts as `FareRuleDetail`, except machine-readable: a time window
 * before departure and what the airline charges inside it. `FareRuleDetail` is
 * supplier-authored HTML whose quality varies from a full fee table to the
 * literal string "The FareBasisCode is: WYMY0", so this is the only source
 * that can be relied on to draw a cancellation / date-change table.
 *
 * Every field stays a STRING as TBO sends it. `details` carries "3000" on one
 * fare and "Non Refundable" on the next, `from`/`to` are numbers in `unit`
 * (Hours or Days), and coercing any of them here would either throw away the
 * words or invent a number the airline never quoted.
 */
export const TboFlightMiniFareRuleSchema = z.object({
  /** "DEL-BOM" - the sector the band applies to. */
  journeyPoints: z.string(),
  /** "Cancellation" / "Reissue" (TBO's word for a date change). */
  type: z.string(),
  from: z.string(),
  to: z.string(),
  /** "Hours" / "Days". */
  unit: z.string(),
  /** The charge, or the airline's words for it. */
  details: z.string(),
  /** Null when TBO omits the flag rather than saying no. */
  onlineReissueAllowed: z.boolean().nullable(),
  onlineRefundAllowed: z.boolean().nullable(),
});
export type TboFlightMiniFareRule = z.infer<typeof TboFlightMiniFareRuleSchema>;

export const TboFlightFareRuleResponseSchema = z.object({
  /** Rendered HTML / plain text fare rules per leg. */
  rules: z.array(
    z.object({
      origin: z.string(),
      destination: z.string(),
      bodyHtml: z.string(),
      /** The booking class the rules belong to, where TBO names it. */
      fareBasisCode: z.string().nullable().default(null),
    }),
  ),
  /**
   * Structured penalty bands, flattened across sectors. Empty whenever the
   * airline publishes none - which is common enough that the UI must still
   * work from `rules` alone.
   */
  miniRules: z.array(TboFlightMiniFareRuleSchema).default([]),
});
export type TboFlightFareRuleResponse = z.infer<typeof TboFlightFareRuleResponseSchema>;

export const TboFlightFareQuoteInputSchema = z.object({
  resultIndex: z.string().trim().min(1),
  traceId: z.string().trim().min(1),
  /** Currency the quote should be returned in (converted live). Default INR. */
  currencyCode: z.string().trim().length(3).default('INR'),
});
export type TboFlightFareQuoteInput = z.infer<typeof TboFlightFareQuoteInputSchema>;

export const TboFlightFareQuoteResponseSchema = z.object({
  /** The locked fare for the next 5-15 minutes. */
  /** Numeric-string paise (see tbo-hotel.ts for the rationale; same here). */
  totalFarePaise: z.string().regex(/^-?\d+$/, 'totalFarePaise must be a whole-number string'),
  /** Supplier base fare, in paise. See `TboFlightOptionSchema.basePaise`. */
  basePaise: z.string().regex(/^-?\d+$/, 'basePaise must be a whole-number string'),
  /** Supplier taxes/surcharges, in paise. See `TboFlightOptionSchema.taxPaise`. */
  taxPaise: z.string().regex(/^-?\d+$/, 'taxPaise must be a whole-number string'),
  currencyCode: z.string(),
  isPriceChanged: z.boolean(),
  /** New resultIndex if TBO rotated it during quote (rare). */
  resultIndex: z.string(),
  /** Quote expiry (ISO timestamp). */
  expiresAt: z.string().nullable(),
});
export type TboFlightFareQuoteResponse = z.infer<typeof TboFlightFareQuoteResponseSchema>;

// ---- Fare upsell (branded fare families after Search) --------------------

export const TboFlightFareUpsellInputSchema = z.object({
  resultIndex: z.string().trim().min(1),
  traceId: z.string().trim().min(1),
  /** Currency prices should be returned in (converted live). Default INR. */
  currencyCode: z.string().trim().length(3).default('INR'),
});
export type TboFlightFareUpsellInput = z.infer<typeof TboFlightFareUpsellInputSchema>;

/** One included / chargeable service on a branded fare family. */
export const TboFlightFareUpsellServiceSchema = z.object({
  code: z.string(),
  isChargeable: z.boolean(),
  isIncluded: z.boolean(),
  serviceType: z.string().nullable(),
  ssrCode: z.string().nullable(),
  description: z.string(),
});
export type TboFlightFareUpsellService = z.infer<typeof TboFlightFareUpsellServiceSchema>;

/**
 * One fare-family card (Saver / Flexi / Super Flexi). Drives the "View Fares"
 * upsell UI. Call FareQuote with `resultIndex` after the traveller picks one.
 */
export const TboFlightFareUpsellOptionSchema = z.object({
  fareFamilyCode: z.string(),
  fareFamilyName: z.string(),
  fareRuleIndex: z.number().int().nullable(),
  flightInfoIndex: z.number().int().nullable(),
  passengerType: z.number().int().nullable(),
  /**
   * Price for this family when TBO attaches one. Null when only the parent
   * Results.Fare is priced — UI falls back to `totalFarePaise` on the parent.
   */
  totalFarePaise: z
    .string()
    .regex(/^-?\d+$/, 'totalFarePaise must be a whole-number string')
    .nullable(),
  /** Supplier base fare, in paise. Present exactly when `totalFarePaise` is. */
  basePaise: z
    .string()
    .regex(/^-?\d+$/, 'basePaise must be a whole-number string')
    .nullable(),
  /** Supplier taxes/surcharges, in paise. Present exactly when `totalFarePaise` is. */
  taxPaise: z
    .string()
    .regex(/^-?\d+$/, 'taxPaise must be a whole-number string')
    .nullable(),
  currencyCode: z.string().nullable(),
  isRefundable: z.boolean().nullable(),
  /** Rotated ResultIndex for this family when TBO returns one; else null. */
  resultIndex: z.string().nullable(),
  services: z.array(TboFlightFareUpsellServiceSchema),
});
export type TboFlightFareUpsellOption = z.infer<typeof TboFlightFareUpsellOptionSchema>;

export const TboFlightFareUpsellResponseSchema = z.object({
  /** ResultIndex to echo into FareQuote / Book (may rotate from search). */
  resultIndex: z.string(),
  isPriceChanged: z.boolean(),
  isRefundable: z.boolean(),
  isLcc: z.boolean(),
  /** Numeric-string paise for the Results.Fare snapshot. */
  totalFarePaise: z.string().regex(/^-?\d+$/, 'totalFarePaise must be a whole-number string'),
  /** Supplier base fare, in paise. See `TboFlightOptionSchema.basePaise`. */
  basePaise: z.string().regex(/^-?\d+$/, 'basePaise must be a whole-number string'),
  /** Supplier taxes/surcharges, in paise. See `TboFlightOptionSchema.taxPaise`. */
  taxPaise: z.string().regex(/^-?\d+$/, 'taxPaise must be a whole-number string'),
  currencyCode: z.string(),
  /** Branded fare families for the Saver / Flexi / Super Flexi cards. */
  options: z.array(TboFlightFareUpsellOptionSchema),
});
export type TboFlightFareUpsellResponse = z.infer<typeof TboFlightFareUpsellResponseSchema>;

// ---- Fare calendar ------------------------------------------------------

/**
 * One day on the two-month fare strip.
 *
 * A *cached* figure, not a live quote: see `FareCalendarService` for why sixty
 * live searches per page view is not available to us. `fetchedAt` travels with
 * every entry precisely because of that — the UI is expected to say how old the
 * number is rather than present it as today's price.
 */
export const FlightFareCalendarDaySchema = z.object({
  /** `YYYY-MM-DD`. */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** Cheapest whole-journey fare seen for this date, in minor units. */
  pricePaise: z.string().regex(/^-?\d+$/, 'pricePaise must be a whole-number string'),
  currencyCode: z.string(),
  /** ISO timestamp of the search that produced it. */
  fetchedAt: z.string(),
});
export type FlightFareCalendarDay = z.infer<typeof FlightFareCalendarDaySchema>;

export const FlightFareCalendarQuerySchema = z.object({
  origin: z
    .string()
    .length(3)
    .transform((v) => v.toUpperCase()),
  destination: z
    .string()
    .length(3)
    .transform((v) => v.toUpperCase()),
  /** First date on the strip, `YYYY-MM-DD`. */
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /**
   * How many days to return. Capped server-side at 62 — two months is the
   * window the strip draws, and a larger one would be a cache read nobody
   * renders.
   */
  days: z.coerce.number().int().min(1).max(62).default(62),
  cabinClass: TboFlightCabinClassSchema.default('ECONOMY'),
});
export type FlightFareCalendarQuery = z.input<typeof FlightFareCalendarQuerySchema>;

export const FlightFareCalendarResponseSchema = z.object({
  origin: z.string(),
  destination: z.string(),
  cabinClass: TboFlightCabinClassSchema,
  /**
   * Only the dates we actually hold a fare for.
   *
   * Dates with nothing cached are omitted rather than returned as nulls: the
   * client knows the range it asked for and draws its own empty cells, and
   * sending sixty nulls to convey nothing would triple the payload.
   */
  days: z.array(FlightFareCalendarDaySchema),
});
export type FlightFareCalendarResponse = z.infer<typeof FlightFareCalendarResponseSchema>;
